import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/ApiProvider.js';
import { ApiError } from '../api/client.js';
import { Modal } from '../components/Modal/Modal.js';
import { describeError } from './shared.js';
import styles from './Rewards.module.css';

const dateFmt = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/**
 * Read-only audit log for one class's jar: every point change with its amount
 * (+2, −1, …) and when it happened, newest first. Lets the teacher spot a
 * mis-tap like an accidental double point.
 */
export function HistoryModal(props: {
  open: boolean;
  onClose: () => void;
  classId: string;
  teacherName: string;
}) {
  const api = useApi();
  const query = useQuery({
    queryKey: ['tech-time', 'history', props.classId],
    queryFn: () => api.techTime.listHistory(props.classId),
    enabled: props.open,
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 2,
  });

  const events = query.data ?? [];

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title={`${props.teacherName} — point history`}
      description="Every change to the jar, newest first."
      testId="rewards-history"
    >
      {query.isLoading ? <p className={styles.loading}>Loading…</p> : null}

      {query.error ? (
        <div className={styles.errorBanner} role="alert">
          Couldn’t load the history — {describeError(query.error)}
        </div>
      ) : null}

      {!query.isLoading && !query.error && events.length === 0 ? (
        <p className={styles.hint} data-testid="rewards-history-empty">
          No changes recorded yet. New points will show up here.
        </p>
      ) : null}

      {events.length > 0 ? (
        <ul className={styles.historyList} data-testid="rewards-history-list">
          {events.map((e) => {
            const isReset = e.delta < 0 && e.progress === 0;
            return (
              <li key={e.id} className={styles.historyItem} data-testid="rewards-history-item">
                <span
                  className={`${styles.historyDelta} ${e.delta < 0 ? styles.historyDeltaNeg : ''}`}
                >
                  {e.delta > 0 ? `+${e.delta}` : `−${Math.abs(e.delta)}`}
                </span>
                <span className={styles.historyMeta}>
                  <span className={styles.historyTotal}>
                    {isReset ? 'Reset to 0' : `${e.progress} point${e.progress === 1 ? '' : 's'}`}
                  </span>
                  <span className={styles.historyDate}>
                    {dateFmt.format(new Date(e.createdAt))}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </Modal>
  );
}
